import React, { useState } from 'react';
import {
  Container,
  Paper,
  TextField,
  Button,
  Typography,
  Box,
  Alert,
  CircularProgress,
  Link
} from '@mui/material';
import { useAuth } from '../contexts/AuthContext';
import { useNavigate } from 'react-router-dom';
import PasswordChangeDialog from '../components/PasswordChangeDialog';

const LoginSSO = () => {
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [error, setError] = useState('');
  const [loading, setLoading] = useState(false);
  const [showClassicLogin, setShowClassicLogin] = useState(false);
  const [showPasswordChangeDialog, setShowPasswordChangeDialog] = useState(false);
  const [tempPasswordUser, setTempPasswordUser] = useState(null);
  const { login } = useAuth();
  const navigate = useNavigate();

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError('');
    setLoading(true);

    const result = await login(email, password);

    if (result.success) {
      if (result.user.role === 'Dealer' && result.user.password_tmp === 'YES') {
        setTempPasswordUser(result.user);
        setShowPasswordChangeDialog(true);
        setLoading(false);
        return;
      }

      if (result.user.role === 'SuperAdmin' || result.user.role === 'Sales') {
        navigate('/admin');
      } else if (['Admin', 'Manager'].includes(result.user.role)) {
        navigate('/manufacturer');
      } else if (result.user.role === 'Dealer') {
        navigate('/dealer');
      } else {
        navigate('/');
      }
    } else {
      setError(result.error);
    }

    setLoading(false);
  };

  const handlePasswordChangeSuccess = () => {
    if (tempPasswordUser) {
      navigate('/dealer');
    }
    setTempPasswordUser(null);
  };

  const handlePasswordChangeClose = () => {
    setShowPasswordChangeDialog(false);
    setTempPasswordUser(null);
  };

  const isSSOView = !showClassicLogin;

  return (
    <Box
      sx={{
        minHeight: '100vh',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        py: 4,
        px: 2,
        background: 'linear-gradient(135deg, #f5f5f5 0%, #e8eaf6 50%, #f5f5f5 100%)'
      }}
    >
      <Container maxWidth="md">
        <Paper
          elevation={8}
          sx={{
            overflow: 'hidden',
            borderRadius: 3,
            display: 'flex',
            flexDirection: { xs: 'column', sm: 'row' },
            minHeight: 420
          }}
        >
          <Box
            sx={{
              p: 4,
              flex: '1 1 320px',
              display: 'flex',
              flexDirection: 'column',
              justifyContent: 'center'
            }}
          >
            <Typography
              variant="h4"
              component="h1"
              gutterBottom
              align="center"
              sx={{ fontWeight: 600, color: 'text.primary' }}
            >
              Contest Platform
            </Typography>

            {error && (
              <Alert severity="error" sx={{ mb: 2 }} onClose={() => setError('')}>
                {error}
              </Alert>
            )}

            <Box sx={{ mt: 2 }}>
              {isSSOView ? (
                <>
                  <Button
                    type="button"
                    fullWidth
                    variant="contained"
                    size="large"
                    sx={{
                      mb: 2,
                      py: 1.5,
                      fontSize: '1rem',
                      fontWeight: 600,
                      textTransform: 'none',
                      boxShadow: 2,
                      '&:hover': { boxShadow: 4 }
                    }}
                    disabled={loading}
                    onClick={() => navigate('/sso')}
                  >
                    Sign in with Dealer Portal
                  </Button>
                  <Box sx={{ textAlign: 'center', mt: 2 }}>
                    <Link
                      component="button"
                      variant="body2"
                      onClick={() => setShowClassicLogin(true)}
                      sx={{ cursor: 'pointer', color: 'primary.main', fontWeight: 500 }}
                      type="button"
                    >
                      Other authentication
                    </Link>
                  </Box>
                </>
              ) : (
                <Box component="form" onSubmit={handleSubmit}>
                  <TextField
                    fullWidth
                    label="Email"
                    type="text"
                    inputProps={{ inputMode: 'email' }}
                    autoComplete="email"
                    value={email}
                    onChange={(e) => setEmail(e.target.value)}
                    margin="normal"
                    required
                    autoFocus
                  />

                  <TextField
                    fullWidth
                    label="Password"
                    type="password"
                    value={password}
                    onChange={(e) => setPassword(e.target.value)}
                    margin="normal"
                    required
                  />

                  <Button
                    type="submit"
                    fullWidth
                    variant="contained"
                    size="large"
                    sx={{ mt: 3, mb: 2, py: 1.5, fontWeight: 600, textTransform: 'none', boxShadow: 2 }}
                    disabled={loading}
                  >
                    {loading ? <CircularProgress size={24} /> : 'Login'}
                  </Button>

                  <Box sx={{ textAlign: 'center', mt: 2 }}>
                    <Link
                      component="button"
                      variant="body2"
                      onClick={() => setShowClassicLogin(false)}
                      sx={{ cursor: 'pointer', color: 'primary.main', fontWeight: 500 }}
                      type="button"
                    >
                      Sign in with Dealer Portal
                    </Link>
                  </Box>
                </Box>
              )}
            </Box>
          </Box>

          {isSSOView && (
            <Box
              sx={{
                flex: '1 1 50%',
                minHeight: 280,
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                p: 2,
                bgcolor: 'grey.900'
              }}
            >
              <img
                src="/login-image.png"
                alt=""
                style={{
                  maxWidth: '100%',
                  maxHeight: 380,
                  height: 'auto',
                  objectFit: 'contain',
                  borderRadius: 8
                }}
              />
            </Box>
          )}
        </Paper>
      </Container>
      <PasswordChangeDialog
        open={showPasswordChangeDialog}
        onClose={handlePasswordChangeClose}
        onSuccess={handlePasswordChangeSuccess}
        user={tempPasswordUser}
      />
    </Box>
  );
};

export default LoginSSO;
